import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { HelpCircle, Search, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * CategoryReferenceGuide - a reusable popup that shows all categories
 * and their typical items so operators know what fits where.
 *
 * Usage:
 *   <CategoryReferenceGuide inventoryType="internal" />
 */
export default function CategoryReferenceGuide({ inventoryType, triggerLabel = 'Category Guide' }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchCategories();
    }
  }, [open, inventoryType]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const params = inventoryType ? `?inventory_type=${inventoryType}` : '';
      const response = await axios.get(`${API}/categories${params}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = categories.filter((cat) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const inName = cat.name.toLowerCase().includes(q);
    const inPrefix = cat.prefix.toLowerCase().includes(q);
    const inDesc = (cat.description || '').toLowerCase().includes(q);
    const inItems = (cat.typical_items || []).some((item) =>
      item.toLowerCase().includes(q)
    );
    return inName || inPrefix || inDesc || inItems;
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5 border-[#00a89c] text-[#00a89c] hover:bg-teal-50 hover:text-[#00a89c]"
          data-testid="category-guide-button"
        >
          <HelpCircle className="h-4 w-4" />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-[#1680e5]" />
            Category Reference Guide
          </DialogTitle>
          <DialogDescription>
            Not sure which category to use? Search below or browse the list to find where your item fits.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by item name, category, or prefix (e.g., 'helmet', 'PPE')"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="category-guide-search"
          />
        </div>

        <div className="overflow-y-auto pr-2 -mr-2 space-y-3 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1680e5]"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No categories match &quot;{search}&quot;
            </div>
          ) : (
            filtered.map((cat) => (
              <Card key={cat.id} className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-slate-900">{cat.name}</h3>
                        <code className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded font-semibold text-slate-700">
                          {cat.prefix}-####
                        </code>
                        {!cat.active && (
                          <Badge variant="secondary" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      {cat.description && (
                        <p className="text-sm text-slate-600 mt-1">{cat.description}</p>
                      )}
                    </div>
                  </div>
                  {cat.typical_items && cat.typical_items.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wide">
                        Typical items
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {cat.typical_items.map((item, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="font-normal text-slate-700 bg-slate-50"
                          >
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
